
import React, { useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { SUBSCRIPTION_STATUSES } from 'constants/statuses'

import { date, toDollar, getErrorMessage, buildExternalLink, openExternalLink, EXTERNAL_LINK_BASES, storage, capitalize } from 'utils'

import { useCanAccess, useDisclosure } from 'hooks'
import { useCompanyQuery, useVeraSpaceMutationsQuery, useCompanyMutationsQuery } from 'hooks/api'
import { useVeraSpaceStore } from 'stores'
import { DoorOpenIcon, OctagonPauseIcon, CircleCheckIcon, CircleXIcon, ArchiveIcon, ArchiveRestoreIcon, UserPlusIcon, ChevronDownIcon, GiftIcon, CoinsIcon, RadioTowerIcon } from 'lucide-react'

import { BRAND_ICONS } from './brand-icons'

import { ROUTES } from 'routes'
import { CompanyPageSkeleton } from './CompanyPageSkeleton'
import { useTheme, Stack, Typography } from '@mui/material'
import { Alert, Button, MenuButton, Tabs, TABS_CONTENT_GAP } from '@repo/ui'
import {
  Header,
  InviteInternalUserModal,
  SuspendAccessModal,
  CancelSubscriptionInternalModal,
  CompSubscriptionModal,
  GrantActivationsModal,
  UnsuspendCompanyModal,
  ArchiveCompanyModal,
  ActionDropdown,
  type ActionDropdownItem,
  InfoBlock,
  InfoRow,
  InfoItem,
  BillingOverview,
} from 'components'
import { useSnackbar } from 'contexts'
import {
  OrdersPanel,
  CompanyInvoicesPanel,
  CompanyMembersPanel,
  CompanySupportPanel,
  CompanyToursPanel,
  CompanyAskPanel,
  CompanyReferralsPanel,
} from './components'
import { useCompanyReferrals } from 'hooks/api/useInternalReferrals'
import { referredByOf } from '../SignupsPage/referrals/referralLabels'
// VRG-675 — the company's source channel (GTM → Sales attribution).
import { useSetCompanySourceChannel, useSourceChannels } from 'hooks/api/useGtmSales'
import { sourceChannelLabel } from 'constants/sourceChannels'
import { SourceChannelModal } from 'components/feedback/modals/SourceChannelModal'


export const CompanyPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  // Where "Return to Internal" comes back to: the page they were ACTUALLY on,
  // path + query so filters and pagination survive (§42). Detail pages included.
  const location = useLocation()
  const theme = useTheme()
  const { open: notification } = useSnackbar()

  const [openInviteInternalUserModal, { open: handleOpenInviteInternalUserModal, close: handleCloseInviteInternalUserModal }] = useDisclosure(false)
  const [openSuspendAccessModal, setOpenSuspendAccessModal] = useState(false)
  const [openCancelSubscriptionModal, setOpenCancelSubscriptionModal] = useState(false)
  const [openCompSubscriptionModal, setOpenCompSubscriptionModal] = useState(false)
  const [openGrantActivationsModal, setOpenGrantActivationsModal] = useState(false)
  const [openSourceChannelModal, setOpenSourceChannelModal] = useState(false)
  const [openUnsuspendModal, setOpenUnsuspendModal] = useState(false)
  const [openArchiveModal, setOpenArchiveModal] = useState(false)
  // VERA-873: tab is reflected in the URL (?tab=) so it's deep-linkable + back/forward friendly.
  //
  // VRG-516: the 'plan' tab's LABEL is now "Billing"; its VALUE stays `plan`, and the
  // divergence is deliberate rather than an oversight. `?tab=plan` is a real
  // deep link — it is what the URL shows after a click, it is what a staffer
  // bookmarks, and `docs/guides/internal-companies.md` documents it by name. A
  // value rename degrades silently rather than erroring: an old bookmark falls
  // through the `includes()` check below and lands on Details, which reads as
  // "the tab moved" rather than "your link is stale". The value is never seen by
  // a user, so the label is the only half that had to change.
  const [searchParams, setSearchParams] = useSearchParams()
  // VRG-516: `support`, `tours` and `ask` join the set. They are LAST rather than
  // interleaved so an existing `?tab=` deep link keeps meaning what it meant.
  const TAB_VALUES = ['details', 'plan', 'invoices', 'orders', 'users', 'referrals', 'support', 'tours', 'ask'] as const
  type CompanyTab = (typeof TAB_VALUES)[number]
  const tabParam = searchParams.get('tab') as CompanyTab | null
  const tab: CompanyTab = tabParam && TAB_VALUES.includes(tabParam) ? tabParam : 'details'
  const setTab = (next: CompanyTab) => {
    setSearchParams(
      prev => {
        const params = new URLSearchParams(prev)
        params.set('tab', next)
        return params
      },
      { replace: true },
    )
  }

  const { data: company, isLoading, refetch } = useCompanyQuery({ id: id as string })
  // VRG-658 — "Referred by" on Details. Same query the Referrals tab reads, so opening the
  // tab after Details costs nothing.
  const { data: companyReferrals, isLoading: referralsLoading } = useCompanyReferrals(id)
  const referredBy = referredByOf(companyReferrals?.referred_by)
  const referredByValue = referralsLoading
    ? undefined
    : referredBy
      ? `${referredBy.primary}${referredBy.secondary ? ` · ${referredBy.secondary}` : ''}`
      : null
  const enterSpace = useVeraSpaceStore((s) => s.enter)
  const { enterAsync: fetchVeraGrant } = useVeraSpaceMutationsQuery({
    onEnterError: () => {
      notification({ type: 'error', title: 'Failed to enter company space.' })
    },
  })
  const { isHasAccess: isPlatformAdmin } = useCanAccess({ role: 'platform_admin' })
  // VRG-675 — explicit company channel only. A company with none counts in GTM → Sales under
  // its signup's channel; that inheritance is the pipeline's, and this value says what was SET.
  const sourceChannels = useSourceChannels()
  const setSourceChannel = useSetCompanySourceChannel()
  const sourceChannel = id ? sourceChannels.data?.companies[id] ?? null : null
  const { isHasAccess: canCompSubscription } = useCanAccess({ action: 'internal_company_change_subscription' })
  const { isHasAccess: canGrantActivations } = useCanAccess({ action: 'internal_company_add_cert_credits' })
  // VRG-516 — the three insight tabs, gated on the SAME permissions their endpoints
  // enforce (VRG-515 chose them; this mirrors rather than invents). Support takes
  // `internal_support_manage`; Tours and Ask stats take `internal_analytics_view`
  // (platform_admin only). Ask stats moved off the support grant on 2026-10-08
  // (Spencer, following VRG-685: platform_manager does not see Analytics or Ask stats,
  // here included; the API answers a manager 403). A staffer can hold one grant and not
  // the other, so the three tabs can legitimately differ.
  //
  // HIDDEN, not disabled+tooltip. §46 makes permission a disable-with-tooltip case,
  // but its stated carve-out is a permission nobody in that context can ever hold —
  // and these are `/internal/*` Vera-staff surfaces, so for a company user there is
  // no admin who could grant them and a "ask an admin" tooltip would be a false
  // promise. §27 says the same thing from the other side: whole surfaces hide.
  const { isHasAccess: canViewSupport } = useCanAccess({ action: 'internal_support_manage' })
  const { isHasAccess: canViewTourAnalytics } = useCanAccess({ action: 'internal_analytics_view' })
  const canViewAskStats = canViewTourAnalytics
  // VRG-512 — the Orders tab now calls `GET /shop/orders`, which is gated on
  // `internal_order_view` (platform_admin + vera_fulfillment only). Same
  // hidden-not-disabled reasoning as the three tabs above: this is an `/internal/*`
  // Vera-staff surface, so for anyone without it there is no admin who could grant it
  // and a "ask an admin" tooltip would be a false promise (§46's carve-out, §27).
  // Before this the tab was always visible and always empty, which read as "this
  // company has no orders" rather than "you cannot see orders".
  const { isHasAccess: canViewOrders } = useCanAccess({ action: 'internal_order_view' })

  // Unarchive mutation (direct — no modal needed, mirrors CompaniesPage pattern)
  const { unarchive: unarchiveCompany } = useCompanyMutationsQuery({
    onSuccess: () => {
      refetch()
    },
    onError: (error) => {
      notification({ type: 'error', title: getErrorMessage(error) })
    },
  })

  // VRG-512: `orders` is gone from here. It read `company?.orders`, and
  // `GetCompanyResponseDto` has no `orders` member — so the Orders tab was `[]` for
  // every company and had never once rendered a row. It now fetches its own
  // company-scoped data (see OrdersPanel), the way CompanyMembersPanel already does,
  // rather than being bolted onto the company payload that every page load fetches.
  const invoices = company?.invoices || []


  // Footprint-parity placeholder (§19) in place of the centred <Loader />, which reserved none of the
  // 133px header, the 44px tab bar or the 170px details panel. See CompanyPageSkeleton.
  if (isLoading) {
    return <CompanyPageSkeleton />
  }

  if (!company) {
    navigate(ROUTES.INTERNAL_COMPANIES, { replace: true })
    return null
  }

  const handleGoIntoSpace = async () => {
    if (!company?.id) return
    try {
      // VERA-876: RBAC-as-self space entry (same as the Companies list). Fetch the
      // brand's Vera-Manager grant, set the space store (drives the banner + the
      // permission cap + brand-mode routing), then navigate in-app to the brand's
      // Settings. No token mint, no window.location redirect (that caused the
      // sign-in flash + dropped the space state).
      const grant = await fetchVeraGrant({ companyId: company.id })
      enterSpace({
        companyId: company.id,
        companyName: grant.companyName,
        permissions: grant.permissions,
        // The LIST, not this detail page (VRG-369). Returning to
        // `/internal/companies/:id` would be nicer when it resolves, but the
        // record can be archived, deleted or purged while the staffer is inside
        // its space, and landing on a row that no longer exists is worse than
        // landing on the list it was in. The allowlist in useVeraSpaceStore
        // enforces this rather than trusting each call site.
        returnTo: `${location.pathname}${location.search}`,
      })
      storage.setItem('selectedCompany', company.id)
      navigate(ROUTES.SETTINGS_COMPANY_GENERAL)
    } catch (err) {
      console.error('Failed to enter company space', err)
    }
  }

  const handleSuspendAccess = () => {
    setOpenSuspendAccessModal(true)
  }

  const handleCloseSuspendAccessModal = () => {
    setOpenSuspendAccessModal(false)
  }

  const handleCancelSubscription = () => {
    setOpenCancelSubscriptionModal(true)
  }

  const handleCloseCancelSubscriptionModal = () => {
    setOpenCancelSubscriptionModal(false)
  }

  const handleCompSubscription = () => {
    setOpenCompSubscriptionModal(true)
  }

  const handleCloseCompSubscriptionModal = () => {
    setOpenCompSubscriptionModal(false)
  }

  const handleGrantActivations = () => {
    setOpenGrantActivationsModal(true)
  }

  const handleCloseGrantActivationsModal = () => {
    setOpenGrantActivationsModal(false)
  }

  const handleUnsuspend = () => {
    setOpenUnsuspendModal(true)
  }

  const handleCloseUnsuspendModal = () => {
    setOpenUnsuspendModal(false)
  }

  const handleArchive = () => {
    setOpenArchiveModal(true)
  }

  const handleCloseArchiveModal = () => {
    setOpenArchiveModal(false)
  }

  const handleUnarchive = () => {
    if (!company?.id) return
    unarchiveCompany({ companyId: company.id })
  }

  // VRG-516 — the Details "Kind" value. Expiry is folded in rather than given its
  // own field so a standard company does not carry a permanently-blank row.
  //
  // A demo with NO `demo_expires_at` reads "never expires", NOT a dash: null is the
  // DEFAULT an AE gets at provisioning and is a positive answer (VRG-306), so a dash
  // would misreport a decided lifetime as missing data.
  // Three cases, not two. `date()` returns `string | null` (null on an unparseable
  // value), and an expiry that is SET but unformattable must not fall through to
  // "never expires" — that would misreport a decided lifetime as an unlimited one,
  // the exact inversion of the error the "never expires" wording exists to prevent.
  // A bare "Demo" is the honest answer there.
  const demoExpiry = company.demo_expires_at ? date(company.demo_expires_at) : null
  const demoLabel = demoExpiry
    ? `Demo (expires ${demoExpiry})`
    : company.demo_expires_at
      ? 'Demo'
      : 'Demo (never expires)'
  const kindValue = company.kind === 'demo'
    ? demoLabel
    : company.kind
      ? capitalize(company.kind)
      : null

  const isArchived = !!company.archived
  const hasStripeSubscription = !!company.subscription?.stripe_subscription_id

  // Standing rule (§46): permission and record state are gated differently —
  // permission stays disabled+tooltip (the item is reachable, an admin can
  // grant it), record state hides the item entirely (there is nothing to
  // grant; the resolving action belongs to the user, not the app). A single
  // tooltip used to fold BOTH reasons (no permission, no Stripe subscription)
  // into one `disabled`, which meant "no subscription" showed as a disabled
  // item instead of simply not being offered. Split: the permission half is
  // this tooltip, driving the kebab item's `disabled`; the no-subscription
  // half lives in that item's `visible` (paired with `!isArchived`, below).
  //
  // VRG-516: the combined variant is gone with the duplicate Billing-tab
  // buttons that were its only remaining caller.
  const compSubscriptionPermissionTooltip = !canCompSubscription
    ? 'You need the "Change subscription" permission. Ask an admin to grant it.'
    : undefined
  const grantActivationsTooltip = !canGrantActivations
    ? 'You need the "Add cert credits" permission. Ask an admin to grant it.'
    : null

  // Header layout (Figma 10737-351524): primary actions inline as buttons,
  // the rest collapse into an overflow kebab (honors the >3-actions rule).
  //
  // §27 — in-context actions gated on `platform_admin` stay VISIBLE and go
  // disabled+tooltip (never hidden) so a capped staffer can see the kebab has
  // something in it and knows exactly why it's out of reach. `visible:` is
  // reserved for RECORD STATE (isArchived / subscription presence) — the
  // permission/role check always lands in `disabled` + `tooltip`. "Grant
  // activations" just below is permission-only; "Comp subscription" mixes a
  // record-state precondition in too, see the split note just above.
  const platformAdminTooltip = (verb: string) => (isPlatformAdmin ? undefined : `Only a Vera admin can ${verb}.`)
  const overflowItems: ActionDropdownItem[] = [
    {
      label: 'Invite user',
      onClick: handleOpenInviteInternalUserModal,
      visible: !isArchived,
      disabled: !isPlatformAdmin,
      tooltip: platformAdminTooltip('invite a user'),
      icon: <UserPlusIcon strokeWidth={1} />,
    },
    {
      label: 'Cancel subscription',
      onClick: handleCancelSubscription,
      visible: !!company.subscription?.stripe_subscription_id,
      disabled: !isPlatformAdmin,
      tooltip: platformAdminTooltip('cancel a subscription'),
      destructive: true,
      icon: <CircleXIcon strokeWidth={1} color={theme.palette.error.main} />,
    },
    {
      label: 'Comp subscription',
      onClick: handleCompSubscription,
      // Record state (no Stripe subscription to comp) hides the item; only
      // permission stays disabled+tooltip. See the note above.
      visible: !isArchived && hasStripeSubscription,
      disabled: !!compSubscriptionPermissionTooltip,
      tooltip: compSubscriptionPermissionTooltip,
      icon: <GiftIcon strokeWidth={1} />,
    },
    {
      label: 'Grant activations',
      onClick: handleGrantActivations,
      visible: !isArchived,
      disabled: !!grantActivationsTooltip,
      tooltip: grantActivationsTooltip ?? undefined,
      icon: <CoinsIcon strokeWidth={1} />,
    },
    {
      label: 'Set source channel',
      onClick: () => setOpenSourceChannelModal(true),
      visible: true,
      disabled: !isPlatformAdmin,
      tooltip: platformAdminTooltip("set a company's source channel"),
      icon: <RadioTowerIcon strokeWidth={1} />,
    },
    ...(isArchived ? [{
      label: 'Unarchive',
      onClick: handleUnarchive,
      visible: true,
      disabled: !isPlatformAdmin,
      tooltip: platformAdminTooltip('unarchive a company'),
      icon: <ArchiveRestoreIcon strokeWidth={1} />,
    }] : [{
      label: 'Archive',
      onClick: handleArchive,
      visible: true,
      disabled: !isPlatformAdmin,
      tooltip: platformAdminTooltip('archive a company'),
      icon: <ArchiveIcon strokeWidth={1} />,
    }]),
  ]

  // VERA-873: one "Open in" dropdown for all attached external systems
  // (base URL + stored id; bases live in utils/external-links). Monochrome
  // glyphs (BRAND_ICONS) inherit the menu text colour.
  const externalToolItems = [
    { key: 'apollo', label: 'Apollo', base: EXTERNAL_LINK_BASES.apollo, id: company.apollo_id },
    { key: 'shopify', label: 'Shopify', base: EXTERNAL_LINK_BASES.shopifyCustomer, id: company.shopify_customer_id },
    { key: 'stripe', label: 'Stripe', base: EXTERNAL_LINK_BASES.stripeCustomer, id: company.stripe_customer_id },
  ].filter(({ id }) => !!id)
  const hasExternalTools = externalToolItems.length > 0

  return (
    <>
    <Header
        avatar={{url: company?.logo, letters: '', isCompany: true, name: company?.name}}
        title={company.name}
        // total={total}
        status={company.subscription ? {
          statuses: SUBSCRIPTION_STATUSES,
          value: company.subscription.status
        } : undefined}
        breadcrumbsOveride={company.name}
        rightComponents={
          <Stack direction="row" spacing="8px" alignItems="center">
            {/* VERA-873: single "Open in" dropdown for all attached external systems.
                Uses the shared @repo/ui Select (consistent item styling / text size)
                with a secondary Button trigger matching Go-into-space / Suspend. */}
            {hasExternalTools && (
              <MenuButton
                button={
                  <Button
                    variant="secondary"
                    endIcon={<ChevronDownIcon strokeWidth={1} />}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Open in
                  </Button>
                }
                items={externalToolItems.map(({ key, label, base, id }) => ({
                  label,
                  value: buildExternalLink(base, id),
                  icon: BRAND_ICONS[key],
                }))}
                onSelect={(item: { value: string | null }) => openExternalLink(item.value)}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              />
            )}
            {!isArchived && (
              <Button
                variant="secondary"
                onClick={handleGoIntoSpace}
                // §21: same action, same icon everywhere. `DoorOpenIcon` matches the
                // Demos console and the Companies list kebab. `ArrowUpRightIcon` is
                // this app's NAVIGATE glyph, and entering a space is not navigation —
                // it is assuming a scoped identity inside another company.
                startIcon={<DoorOpenIcon strokeWidth={1} />}
              >
                Go to space
              </Button>
            )}
            {isPlatformAdmin && !isArchived && (
              company.suspended ? (
                <Button
                  variant="secondary"
                  onClick={handleUnsuspend}
                  startIcon={<CircleCheckIcon strokeWidth={1} />}
                >
                  Unsuspend
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  destructive="hover"
                  onClick={handleSuspendAccess}
                  startIcon={<OctagonPauseIcon strokeWidth={1} />}
                >
                  Suspend
                </Button>
              )
            )}
            <ActionDropdown items={overflowItems} variant="button" />
          </Stack>
        }
      />
    {/* Status banners stay above the tabs so they're always visible */}
    {company.suspended && (
      <Stack pt={6}>
        <Alert
          severity="error"
          description={
            <Stack spacing="2px">
              <Typography variant="body2" fontWeight={600}>Access suspended</Typography>
              {company.suspension_reason && (
                <Typography variant="body2">Reason: {company.suspension_reason}</Typography>
              )}
              {company.suspended_at && (
                <Typography variant="body2">Since: {date(company.suspended_at)}</Typography>
              )}
            </Stack>
          }
        />
      </Stack>
    )}

    {company.subscription?.cancels_at && (
      <Stack pt={company.suspended ? 4 : 6}>
        <Alert
          severity="warning"
          description={`Subscription cancels on ${date(company.subscription.cancels_at)}`}
        />
      </Stack>
    )}

    <Stack pt={3} spacing={6}>
      {/* VRG-470: underline page-header tabs. This page used to pass `rightComponents`
          to hang per-tab actions level with the tab row; VRG-516 deleted those actions
          as duplicates of the header kebab, so the tabs render as a bare row again. */}
      <Tabs
        redirect={false}
        value={tab}
        onChange={(v) => setTab(v as typeof tab)}
        tabs={[
          { label: 'Details', value: 'details' },
          // VRG-516: label "Billing", value `plan` — see TAB_VALUES for why they diverge.
          { label: 'Billing', value: 'plan' },
          { label: 'Invoices', value: 'invoices' },
          // Filtered, not conditionally spread inline, so the permission check reads
          // once per tab instead of as nested ternaries in a literal.
          // VRG-512 keeps Orders in its original position (before Users) rather than
          // appending it to the gated block — a permission changes whether a tab is
          // there, never where it is.
          ...[
            { label: 'Orders', value: 'orders', visible: canViewOrders },
            { label: 'Users', value: 'users', visible: true },
            // VRG-658 — the referrals this company has sent. Every staffer who can open
            // this page can read them (the API admits platform admin + manager).
            { label: 'Referrals', value: 'referrals', visible: true },
            { label: 'Support', value: 'support', visible: canViewSupport },
            { label: 'Tour analytics', value: 'tours', visible: canViewTourAnalytics },
            { label: 'Ask stats', value: 'ask', visible: canViewAskStats },
          ]
            .filter(t => t.visible)
            .map(({ label, value }) => ({ label, value })),
        ]}
        // VRG-516: the Billing tab no longer carries its own "Comp subscription" /
        // "Grant activations" buttons. They were an exact DUPLICATE of the two
        // header kebab items (`overflowItems`, above) — same handlers, same
        // modals — so this is a deletion, not a move, and nothing is lost.
        //
        // The kebab copies are also the better-gated pair (§46): "Comp
        // subscription" HIDES when the company has no Stripe subscription
        // (record state) and only disables on the missing permission, whereas
        // these buttons folded both reasons into one `disabled` and so showed a
        // greyed-out control for a state the user could do nothing about.
      />

      {/* Details — company metadata (was on the old Plan tab; VERA-873) */}
      {tab === 'details' && (
        <Stack spacing={4}>
          {/* VRG-516 — every row is the SAME 4-column grid (`columns={4}`), not a
              `space-between` row that divides itself among however many fields it
              holds. Before, the five rows ran 5 | 3 | 4 | 3 | 2 items, so no two
              rows put a field in the same place and 17 fields read as five unrelated
              strips. Now a short row leaves its trailing cells empty and its fields
              still sit under the row above.

              Row composition is 4 | 4 | 4 | 3 | 2, chosen so no row leaves a single
              orphan in its last cell — a lone field trailing a full row is the thing
              that reads as broken rather than as deliberate whitespace.

              The one field that MOVED is "Join date", from the counts row to the
              addressing row. It was the odd one out among Users / Certificates /
              Owners / Orders (a date sitting in a row of counts), and moving it makes
              the first row purely counts while giving the second row its fourth cell.
              Every other field stays in the cluster it was already in. */}
          <InfoBlock>
            <InfoRow columns={4}>
              <InfoItem label="Users" value={company?.users?.length || 0} />
              <InfoItem label="Certificates" value={company?.certficates?.length || 0} />
              <InfoItem label="Owners" value={company?.owners?.length || 0} />
              <InfoItem label="Orders" value={company?.orders?.length || 0} />
            </InfoRow>
            {/* `?? null`, never `|| '-'`. The hyphen was a SECOND empty-value glyph on
                a card that also renders the canonical em dash, so the same "no value"
                answered to two characters in one panel. `InfoItem` already renders
                `EMPTY_VALUE` for null/blank centrally, which is the single source these
                call sites were bypassing — see `@repo/ui`'s `emptyValue.ts`, whose whole
                docblock is this argument. `?? null` and not a bare optional because
                `InfoItem` reads `undefined` as LOADING and would shimmer forever. */}
            <InfoRow columns={4}>
              <InfoItem label="Join date" value={company.join_date ? date(company.join_date) : null} />
              <InfoItem label="Headquarters" value={company?.headquarters_country ?? null} />
              <InfoItem label="Subdomain" term="subdomain" value={company?.subdomain ?? null} />
              {/* VERA-1095: signup gate — allowed domain for self-signup matching */}
              <InfoItem label="Signup domain" value={company?.signup_allowed_domain ?? null} />
            </InfoRow>
            {/* VRG-516 — classification. Every value below is `?? null`, never a bare
                optional: InfoItem reads `undefined` as LOADING and renders a skeleton,
                so an absent optional field would otherwise sit as a permanent shimmer
                where it should read as a dash. */}
            <InfoRow columns={4}>
              {/* §55 makes "is this a demo?" a first-class question about a company,
                  and it was invisible on this page. Expiry folds into the same value
                  rather than taking its own field, so a standard company (the large
                  majority) does not carry a permanently-blank "Demo expires" row. */}
              <InfoItem label="Kind" value={kindValue} />
              {/* TODO(VRG-529): plan TIER is not in the company payload today —
                  VRG-515 is the ticket adding it, on a parallel branch. Rendering a
                  dash is the honest state until that lands; do NOT derive a tier from
                  `subscription.plan.name` here, which would guess at the very thing
                  that ticket exists to make authoritative. */}
              <InfoItem label="Tier" value={null} />
              <InfoItem
                label="Provisioning"
                value={company.provisioning_status ? capitalize(company.provisioning_status) : null}
              />
              <InfoItem label="Provider" value={company.provider ? capitalize(company.provider) : null} />
            </InfoRow>
            <InfoRow columns={4}>
              {/* VERA-260: declared reporting currency for analytics money metrics.
                  Nothing is FX-converted, so which currency a figure is IN is part of
                  reading it. */}
              <InfoItem label="Reporting currency" value={company.reporting_currency ?? null} />
              {/* Both are self-signup-form fields (see the type), so they are null for
                  every staff-provisioned or seeded company — a dash here means "this
                  company did not self-signup", not "we lost the data". */}
              <InfoItem label="Employee count" value={company.employee_count ?? null} />
              <InfoItem
                label="Categories"
                value={company.categories?.length ? company.categories.join(', ') : null}
              />
            </InfoRow>
            <InfoRow columns={4}>
              <InfoItem label="Support email" value={company.support_email ?? null} />
              <InfoItem label="Support phone" value={company.support_phone ?? null} />
              {/* VRG-658 — who brought this company in: a brand (and the member who sent
                  it) or an Insider. `undefined` while loading (InfoItem shimmers), `null`
                  when nobody did (the em dash). */}
              <InfoItem label="Referred by" value={referredByValue} />
              {/* VRG-675 — set from the header menu; `undefined` while loading (shimmer). */}
              <InfoItem
                label="Source channel"
                value={sourceChannels.isLoading ? undefined : sourceChannel ? sourceChannelLabel(sourceChannel) : null}
              />
            </InfoRow>
          </InfoBlock>
        </Stack>
      )}

      {/* Billing (value `plan`) — parity with the company-settings Billing Overview
          via the shared <BillingOverview> (VERA-873). Usage/invoice figures are
          still placeholder until the admin billing endpoints land (TD-048).
          Comp subscription / Grant activations live in the header kebab only
          (VRG-516) — this tab carries no actions of its own. */}
      {tab === 'plan' && (
        <Stack spacing={4}>
          <BillingOverview
            subscription={company.subscription}
            grantedActivationsTotal={company.grantedActivationsTotal}
          />
        </Stack>
      )}

      {tab === 'invoices' && <CompanyInvoicesPanel invoices={invoices} />}
      {/* VRG-512: re-checked here, not only on the tab, for the same reason the three
          insight tabs below are — a hand-typed `?tab=orders` from someone without
          `internal_order_view` renders nothing rather than firing a request that 403s
          and draws an error panel. The API guard is still the real boundary. */}
      {tab === 'orders' && canViewOrders && (
        <OrdersPanel companyId={company.id} companyName={company.name} />
      )}
      {tab === 'users' && <CompanyMembersPanel companyId={company.id} companyName={company.name} />}
      {tab === 'referrals' && <CompanyReferralsPanel companyId={company.id} />}

      {/* VRG-516 — the three insight tabs (endpoints from VRG-515).
          The permission is re-checked HERE and not only on the tab, so a hand-typed
          `?tab=support` from someone without the permission renders nothing rather
          than firing a request that 403s and drawing an error panel. The API guard is
          still the real boundary; this is just not asking a question we know the
          answer to. */}
      {tab === 'support' && canViewSupport && <CompanySupportPanel companyId={company.id} />}
      {tab === 'tours' && canViewTourAnalytics && <CompanyToursPanel companyId={company.id} />}
      {tab === 'ask' && canViewAskStats && <CompanyAskPanel companyId={company.id} />}
    </Stack>
    <InviteInternalUserModal 
      type="company" 
      open={openInviteInternalUserModal} 
      onClose={handleCloseInviteInternalUserModal} 
    />
    <SuspendAccessModal
      company={company}
      open={openSuspendAccessModal}
      onClose={handleCloseSuspendAccessModal}
      refetch={refetch}
    />
    <CancelSubscriptionInternalModal
      company={company}
      open={openCancelSubscriptionModal}
      onClose={handleCloseCancelSubscriptionModal}
      refetch={refetch}
    />
    <CompSubscriptionModal
      company={company}
      open={openCompSubscriptionModal}
      onClose={handleCloseCompSubscriptionModal}
      refetch={refetch}
    />
    <GrantActivationsModal
      company={company}
      open={openGrantActivationsModal}
      onClose={handleCloseGrantActivationsModal}
      refetch={refetch}
    />
    <UnsuspendCompanyModal
      company={company}
      open={openUnsuspendModal}
      onClose={handleCloseUnsuspendModal}
      refetch={refetch}
    />
    <ArchiveCompanyModal
      company={company}
      open={openArchiveModal}
      onClose={handleCloseArchiveModal}
      refetch={refetch}
    />
    <SourceChannelModal
      subject="company"
      name={company.name}
      initial={sourceChannel}
      open={openSourceChannelModal}
      loading={setSourceChannel.isPending}
      onClose={() => setOpenSourceChannelModal(false)}
      onSave={(channel) =>
        setSourceChannel.mutate(
          { companyId: company.id, sourceChannel: channel },
          {
            onSuccess: () => {
              setOpenSourceChannelModal(false)
              notification({ type: 'success', title: 'Source channel saved' })
            },
            onError: (e) => notification({ type: 'error', title: getErrorMessage(e) }),
          },
        )
      }
    />
  </>
  )
}